import { Controller } from "@hotwired/stimulus"

// Typeahead simple sobre un dataset estático de Components.
// Reemplaza el <select> nativo con un input + dropdown filtrable.
//
// Uso:
//   <div data-controller="component-picker"
//        data-component-picker-options-value="<%= JSON.dump([{id, name, family}, ...]) %>"
//        data-component-picker-name-value="payload[items][N][component_id]"
//        data-component-picker-selected-value="<%= preselected_id %>">
//   </div>
//
// El controller renderiza:
//   - <input type="text"> visible (con autocomplete del nombre del Component)
//   - <ul> dropdown con resultados filtrados
//   - <input type="hidden" name="..."> con el component_id seleccionado (lo que va al server)
export default class extends Controller {
  static values = {
    options: Array,    // [{id, name, family}, ...]
    name: String,      // nombre del hidden field (lo que va al server)
    selected: Number,  // id pre-seleccionado (opcional)
    placeholder: { type: String, default: "Buscá un ingrediente..." }
  }

  connect() {
    this.activeIndex = -1
    this.filtered = []
    this.render()
  }

  render() {
    const selectedOpt = this.optionsValue.find(o => o.id === this.selectedValue)
    this.element.innerHTML = `
      <input type="hidden" name="${this.nameValue}" value="${selectedOpt ? selectedOpt.id : ''}" data-component-picker-target="hidden">
      <input type="text" class="form-control form-control-sm"
             placeholder="${this.placeholderValue}"
             value="${selectedOpt ? this.escape(selectedOpt.name) : ''}"
             data-component-picker-target="input"
             data-action="input->component-picker#onInput focus->component-picker#onFocus blur->component-picker#onBlur keydown->component-picker#onKeydown"
             autocomplete="off">
      <ul data-component-picker-target="dropdown"
          class="list-group position-absolute shadow-sm"
          style="display:none; z-index:1050; max-height:260px; overflow-y:auto; min-width:280px;"></ul>
    `
    this.hiddenInput = this.element.querySelector('input[type=hidden]')
    this.textInput = this.element.querySelector('input[type=text]')
    this.dropdown = this.element.querySelector('ul')
  }

  onInput() {
    const q = this.textInput.value.toLowerCase().trim()
    // Si el usuario edita el texto y ya no matchea el id seleccionado, limpiar el hidden
    const sel = this.optionsValue.find(o => o.id == this.hiddenInput.value)
    if (sel && sel.name.toLowerCase() !== q) this.hiddenInput.value = ''

    if (q.length === 0) {
      this.filtered = this.optionsValue.slice(0, 30)
    } else {
      this.filtered = this.optionsValue
        .filter(o => o.name.toLowerCase().includes(q) || (o.family || '').toLowerCase().includes(q))
        .slice(0, 50)
    }
    this.activeIndex = -1
    this.renderDropdown()
  }

  onFocus() {
    this.onInput()
  }

  onBlur() {
    // Dejamos margen para que el click en una opción se registre
    setTimeout(() => { if (this.dropdown) this.dropdown.style.display = 'none' }, 150)
  }

  onKeydown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      this.activeIndex = Math.min(this.activeIndex + 1, this.filtered.length - 1)
      this.renderDropdown()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, 0)
      this.renderDropdown()
    } else if (e.key === 'Enter') {
      if (this.activeIndex >= 0 && this.filtered[this.activeIndex]) {
        e.preventDefault()
        this.select(this.filtered[this.activeIndex])
      }
    } else if (e.key === 'Escape') {
      this.dropdown.style.display = 'none'
    }
  }

  pick(event) {
    const id = parseInt(event.currentTarget.dataset.id, 10)
    const opt = this.optionsValue.find(o => o.id === id)
    if (opt) this.select(opt)
  }

  select(opt) {
    this.hiddenInput.value = opt.id
    this.textInput.value = opt.name
    this.dropdown.style.display = 'none'
  }

  renderDropdown() {
    if (this.filtered.length === 0) {
      this.dropdown.style.display = 'none'
      return
    }
    this.dropdown.style.display = 'block'
    this.dropdown.innerHTML = this.filtered.map((o, i) => `
      <li class="list-group-item list-group-item-action py-1 ${i === this.activeIndex ? 'active' : ''}"
          data-id="${o.id}" data-action="mousedown->component-picker#pick"
          style="cursor:pointer">
        <strong>${this.escape(o.name)}</strong>
        ${o.family ? `<small class="text-muted ml-2">${this.escape(o.family)}</small>` : ''}
      </li>
    `).join('')
  }

  escape(s) {
    return String(s).replace(/[&<>"']/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]))
  }
}
